import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { ArrowLeft, Copy, Check, ExternalLink, Image, Code } from "lucide-react";

export function AffOfferDetailPage() {
  const { offerId } = useParams({ strict: false }) as { offerId: string };
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["aff-portal", "offer", offerId],
    queryFn: async () => {
      const res = await api.get(`/v1/portal/affiliate/offers/${offerId}`);
      if (!res.ok) throw new Error("Failed to load offer");
      return (await res.json()).data;
    },
  });

  const applyMut = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/v1/portal/affiliate/offers/${offerId}/apply`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to apply");
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["aff-portal", "offer", offerId] }),
  });

  const [copied, setCopied] = useState<string | null>(null);
  function copyToClipboard(text: string, key: string) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  if (isLoading) {
    return <div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-1/3" /><div className="h-64 bg-gray-200 rounded" /></div>;
  }

  if (!data) {
    return <div className="text-center py-16 text-gray-500">Offer not found</div>;
  }

  const { offer, payoutRevenues, trackingLinks, creatives, assignment } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/portal/affiliate/offers" className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{offer.name}</h1>
          <p className="text-sm text-gray-500">Offer ID: {offer.id} &middot; {offer.conversionMethod}</p>
        </div>
      </div>

      {/* Status & Apply */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-500">Status: </span>
            {assignment ? (
              <span className={cn(
                "text-sm font-medium px-2 py-0.5 rounded",
                assignment.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
              )}>
                {assignment.status === "active" ? "Approved" : "Pending Approval"}
              </span>
            ) : (
              <span className="text-sm text-gray-500">Not applied</span>
            )}
          </div>
          {!assignment && (
            <button
              onClick={() => applyMut.mutate()}
              disabled={applyMut.isPending}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {applyMut.isPending ? "Applying..." : "Apply for this Offer"}
            </button>
          )}
        </div>
      </div>

      {/* Tracking Links */}
      {(assignment?.status === "active" || offer.visibility === "public") && trackingLinks && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Your Tracking Links</h3>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Click URL</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={trackingLinks.clickUrl}
                  className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 font-mono"
                />
                <button
                  onClick={() => copyToClipboard(trackingLinks.clickUrl, "click")}
                  className="shrink-0 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  {copied === "click" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Impression URL</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={trackingLinks.impressionUrl}
                  className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 font-mono"
                />
                <button
                  onClick={() => copyToClipboard(trackingLinks.impressionUrl, "imp")}
                  className="shrink-0 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  {copied === "imp" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-400">
              Append URL parameters: <code className="bg-gray-100 px-1 rounded">?sub1=val&amp;sub2=val</code> for sub-id tracking.
            </p>
          </div>
        </div>
      )}

      {/* Payouts */}
      {payoutRevenues?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Payout Structure</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Type</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Payout</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Default</th>
                </tr>
              </thead>
              <tbody>
                {payoutRevenues.map((pr: { id: number; payoutType: string; payoutAmount: number; isDefault: boolean }) => (
                  <tr key={pr.id} className="border-b border-gray-50">
                    <td className="py-2 px-3 text-gray-700 uppercase text-xs">{pr.payoutType}</td>
                    <td className="py-2 px-3 text-right font-medium text-emerald-600">
                      {pr.payoutType.includes("percentage") ? `${pr.payoutAmount}%` : formatCurrency(pr.payoutAmount)}
                    </td>
                    <td className="py-2 px-3">
                      {pr.isDefault && <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">Default</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Creatives */}
      {creatives?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Creatives</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {creatives.map((cr: { id: number; name: string; creativeType: string; resourceUrl?: string; width?: number; height?: number }) => (
              <div key={cr.id} className="border border-gray-200 rounded-lg p-3">
                {cr.resourceUrl && cr.creativeType === "image" && (
                  <img src={cr.resourceUrl} alt={cr.name} className="w-full h-32 object-contain rounded mb-2 bg-gray-50" />
                )}
                <p className="text-sm font-medium text-gray-700 truncate">{cr.name}</p>
                <p className="text-xs text-gray-400">
                  {cr.creativeType} {cr.width && cr.height ? `${cr.width}x${cr.height}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
